import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  ButtonBase,
  Grid,
  Paper,
  Stack,
  Typography
} from "@mui/material"

import {
  ACTOR_META,
  JOURNEY_STEPS,
  JourneyStep,
  JourneyStepId
} from "./guideContent"
import {
  ActorChip,
  BulletList,
  Card,
  CodeLine,
  GuideSection
} from "./guideParts"

function StepButton({
  step,
  selected,
  onSelect
}: {
  step: JourneyStep
  selected: boolean
  onSelect: () => void
}) {
  const color = ACTOR_META[step.actor].color
  return (
    <ButtonBase
      onClick={onSelect}
      aria-pressed={selected}
      sx={{
        width: "100%",
        height: "100%",
        textAlign: "left",
        alignItems: "stretch",
        borderRadius: 2,
        border: 2,
        borderColor: selected ? color : "divider",
        bgcolor: selected ? `${color}0F` : "background.paper",
        p: 1.5,
        transition: "border-color 120ms, background-color 120ms"
      }}
    >
      <Stack spacing={0.75} sx={{ width: "100%" }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: "50%",
              bgcolor: color,
              color: "#FFFFFF",
              fontWeight: 900,
              fontSize: 13,
              display: "grid",
              placeItems: "center",
              flex: "0 0 auto"
            }}
          >
            {step.number}
          </Box>
          <Typography
            variant="subtitle2"
            component="span"
            sx={{ fontWeight: 900 }}
          >
            {step.title}
          </Typography>
        </Stack>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", lineHeight: 1.4 }}
        >
          {step.oneLine}
        </Typography>
        <Box>
          <ActorChip actor={step.actor} />
        </Box>
      </Stack>
    </ButtonBase>
  )
}

function StepDetail({ step }: { step: JourneyStep }) {
  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>
          {step.number}. {step.title}
        </Typography>
        <ActorChip actor={step.actor} />
      </Stack>
      <Typography
        variant="body1"
        sx={{ lineHeight: 1.65, mb: 2, maxWidth: 820 }}
      >
        {step.whatHappens}
      </Typography>

      <Alert severity="info" icon={false} sx={{ mb: 2.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 800 }}>
          Örnek
        </Typography>
        <Typography variant="body2">{step.example}</Typography>
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
            Adım adım
          </Typography>
          <BulletList items={step.details} />
        </Grid>
        <Grid item xs={12} md={5}>
          <Card accent="#8A94A6">
            <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
              v1’de nasıldı?
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", lineHeight: 1.55 }}
            >
              {step.v1}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Accordion
        variant="outlined"
        disableGutters
        sx={{ mt: 2.5, borderRadius: 1, "&:before": { display: "none" } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            Teknik detay: loglarda nerede görünür, kod nerede?
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 800, display: "block", mb: 0.5 }}
              >
                AI Logs’taki adım adları
              </Typography>
              <Stack spacing={0.5}>
                {step.technical.auditSteps.map((item) => (
                  <CodeLine key={item}>{item}</CodeLine>
                ))}
              </Stack>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 800, display: "block", mb: 0.5 }}
              >
                Kod
              </Typography>
              <Stack spacing={0.5}>
                {step.technical.code.map((item) => (
                  <CodeLine key={item}>{item}</CodeLine>
                ))}
              </Stack>
            </Box>
            {!!step.technical.config?.length && (
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, display: "block", mb: 0.5 }}
                >
                  Ayarlar (qpien-ai .env varsayılanları)
                </Typography>
                <Stack spacing={0.5}>
                  {step.technical.config.map((item) => (
                    <CodeLine key={item}>{item}</CodeLine>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Paper>
  )
}

export default function JourneySection({
  selected,
  onSelect
}: {
  selected: JourneyStepId
  onSelect: (id: JourneyStepId) => void
}) {
  const step =
    JOURNEY_STEPS.find((item) => item.id === selected) || JOURNEY_STEPS[0]
  return (
    <GuideSection
      id="journey"
      eyebrow="Ana akış"
      title="Bir mesajın yolculuğu"
      lead="Müşterinin mesajı altı adımdan geçer. Bir adıma tıklayın: ne olduğu, gerçek bir örnek, v1'deki karşılığı ve loglarda nerede göründüğü açılır. Renk, o adımda kimin karar verdiğini gösterir."
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(6, 1fr)"
          },
          gap: 1.5,
          mb: 2
        }}
      >
        {JOURNEY_STEPS.map((item) => (
          <StepButton
            key={item.id}
            step={item}
            selected={item.id === step.id}
            onSelect={() => onSelect(item.id)}
          />
        ))}
      </Box>
      <StepDetail step={step} />
    </GuideSection>
  )
}
